/* =============================================================
   ROUTER
   =============================================================
     AuthLayout   (requiresAnonymous) → sign-in
     AdminLayout  (requiresAdmin)     → dashboard, and list / new /
                                        detail / edit for berths
                                        and companies
     *                                → 404

   The guard awaits `authReady()` before deciding anything. Without
   that, a reload on /berths would bounce a signed-in admin to
   sign-in while their session was still being restored — and then
   bounce them back, which looks like the app logging them out.

   `requiresAdmin`, not `requiresAuth`: in this app a Supabase
   session that is not in platform_admins is not signed in at all.
   See the note in src/lib/auth.ts.
   ============================================================= */

import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import { HOME_ROUTE, patterns, routes } from '@/config/routes'
import { appConfig } from '@/config/app.config'
import { authReady, useAuth } from '@/lib/auth'
import BerthsPage from '@/pages/BerthsPage.vue'
import BerthDetailPage from '@/pages/BerthDetailPage.vue'
import BerthFormPage from '@/pages/BerthFormPage.vue'
import RateFormPage from '@/pages/RateFormPage.vue'
import BlockFormPage from '@/pages/BlockFormPage.vue'
import DashboardPage from '@/pages/DashboardPage.vue'
import VesselsPage from '@/pages/VesselsPage.vue'
import VesselDetailPage from '@/pages/VesselDetailPage.vue'
import VesselFormPage from '@/pages/VesselFormPage.vue'
import CompaniesPage from '@/pages/CompaniesPage.vue'
import CompanyDetailPage from '@/pages/CompanyDetailPage.vue'
import CompanyFormPage from '@/pages/CompanyFormPage.vue'
import BookingsPage from '@/pages/BookingsPage.vue'
import BookingDetailPage from '@/pages/BookingDetailPage.vue'
import NotFoundPage from '@/pages/NotFoundPage.vue'
import SignInPage from '@/pages/SignInPage.vue'

const { status } = useAuth()

const TITLES: Record<string, string> = {
  [routes.signIn]: 'Sign in',
  [routes.dashboard]: 'Dashboard',
  [routes.berths]: 'Berths',
  [routes.berthNew]: 'New berth',
  [routes.vessels]: 'Vessels',
  [routes.vesselNew]: 'New vessel',
  [routes.companies]: 'Companies',
  [routes.companyNew]: 'New company',
  [routes.bookings]: 'Bookings',
}

/** Detail and edit titles come from the record, which the page has and the
 *  router does not — so they fall back to the section name here. */
function sectionTitle(path: string): string | undefined {
  if (path.startsWith(routes.berths)) return 'Berths'
  if (path.startsWith(routes.vessels)) return 'Vessels'
  if (path.startsWith(routes.companies)) return 'Companies'
  if (path.startsWith(routes.bookings)) return 'Bookings'
  return undefined
}

export const router = createRouter({
  history: createWebHistory(),

  routes: [
    {
      path: '/',
      component: AdminLayout,
      meta: { requiresAdmin: true },
      children: [
        { path: '', redirect: HOME_ROUTE },
        { path: routes.dashboard, component: DashboardPage },

        // `new` before `:id`, and `:id` only matches digits, so the two can
        // never be confused in either direction.
        { path: routes.berths, component: BerthsPage },
        { path: routes.berthNew, component: BerthFormPage },
        { path: patterns.berthDetail, component: BerthDetailPage },
        { path: patterns.berthEdit, component: BerthFormPage },
        { path: patterns.berthRateNew, component: RateFormPage },
        { path: patterns.berthRateEdit, component: RateFormPage },
        { path: patterns.berthBlockNew, component: BlockFormPage },

        { path: routes.vessels, component: VesselsPage },
        { path: routes.vesselNew, component: VesselFormPage },
        { path: patterns.vesselDetail, component: VesselDetailPage },
        { path: patterns.vesselEdit, component: VesselFormPage },

        { path: routes.companies, component: CompaniesPage },
        { path: routes.companyNew, component: CompanyFormPage },
        { path: patterns.companyDetail, component: CompanyDetailPage },
        { path: patterns.companyEdit, component: CompanyFormPage },

        { path: routes.bookings, component: BookingsPage },
        { path: patterns.bookingDetail, component: BookingDetailPage },

        { path: '/:pathMatch(.*)*', component: NotFoundPage },
      ],
    },
    {
      path: '/',
      component: AuthLayout,
      meta: { requiresAnonymous: true },
      children: [{ path: routes.signIn, component: SignInPage }],
    },
  ],

  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  await authReady()
  const signedIn = status.value === 'authenticated'

  if (to.meta.requiresAdmin && !signedIn) {
    // Remember where they were headed so sign-in can send them back.
    return { path: routes.signIn, query: to.fullPath === '/' ? {} : { next: to.fullPath } }
  }

  if (to.meta.requiresAnonymous && signedIn) {
    return HOME_ROUTE
  }

  return true
})

router.afterEach((to) => {
  const title = TITLES[to.path] ?? sectionTitle(to.path)
  document.title = title ? appConfig.titleTemplate.replace('%s', title) : appConfig.name
})

/**
 * A session that ends anywhere other than the sign-out button — an expired
 * refresh token, a revoked grant, a sign-out in another tab — has to move
 * the router too, or the admin is left looking at a page whose every
 * request now fails.
 */
watch(status, (next) => {
  if (next === 'anonymous' && router.currentRoute.value.meta.requiresAdmin) {
    void router.replace(routes.signIn)
  }
})
